import React, { useState, useEffect } from 'react';
import axios from 'axios';

const AdminCateringPanel = () => {
  const [chefs, setChefs] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [cateringBookings, setCateringBookings] = useState([]);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    customer_id: '',
    chef_id: '',
    tier: 'premium',
    guest_count: 10,
    date: '',
    event_type: 'corporate',
    service_address: '',
    service_city: '',
    service_state: '',
    service_zip: '',
    special_requests: '',
    dietary_restrictions: ''
  });

  useEffect(() => {
    loadCateringChefs();
    loadCustomers();
    loadCateringBookings();
  }, []);

  const loadCateringChefs = async () => {
    try {
      const response = await axios.get('/catering/chefs/available');
      setChefs(response.data.chefs || []);
    } catch (error) {
      console.error('Failed to load catering chefs:', error);
    }
  };

  const loadCustomers = async () => {
    try {
      const response = await axios.get('/admin/users');
      const customerList = response.data.users.filter(u => u.role === 'customer');
      setCustomers(customerList);
    } catch (error) {
      console.error('Failed to load customers:', error);
    }
  };

  const loadCateringBookings = async () => {
    try {
      const response = await axios.get('/catering/bookings/all');
      setCateringBookings(response.data.bookings || []);
    } catch (error) {
      console.error('Failed to load catering bookings:', error);
    }
  };

  const calculateTotal = () => {
    const tierPricing = { basic: 75, premium: 100, deluxe: 150 };
    const pricePerPlate = tierPricing[formData.tier];
    const platesTotal = pricePerPlate * formData.guest_count;
    const setupFee = 300;
    const deliveryFee = 125;
    const subtotal = platesTotal + setupFee + deliveryFee;
    const operatingFee = Math.floor(subtotal * 0.20);
    const total = subtotal + operatingFee;

    return {
      pricePerPlate,
      platesTotal,
      setupFee,
      deliveryFee,
      subtotal,
      operatingFee,
      total
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post('/catering/booking', formData);
      alert('Catering booking created successfully!');
      setShowCreateForm(false);
      loadCateringBookings();
      // Reset form
      setFormData({
        customer_id: '',
        chef_id: '',
        tier: 'premium',
        guest_count: 10,
        date: '',
        event_type: 'corporate',
        service_address: '',
        service_city: '',
        service_state: '',
        service_zip: '',
        special_requests: '',
        dietary_restrictions: ''
      });
    } catch (error) {
      console.error('Failed to create catering booking:', error);
      alert('Failed to create catering booking: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const totals = calculateTotal();

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">🍽️ Catering Management</h2>
        <button
          onClick={() => setShowCreateForm(!showCreateForm)}
          className="bg-orange-600 hover:bg-orange-700 text-white px-6 py-2 rounded-lg font-semibold"
        >
          {showCreateForm ? 'Cancel' : '+ Create Catering Booking'}
        </button>
      </div>

      {/* Create Catering Form */}
      {showCreateForm && (
        <div className="bg-white rounded-lg shadow-lg p-6">
          <h3 className="text-xl font-bold mb-4">Create Catering Booking</h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              {/* Customer Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Customer</label>
                <select
                  value={formData.customer_id}
                  onChange={(e) => setFormData({...formData, customer_id: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  required
                >
                  <option value="">Select Customer</option>
                  {customers.map(customer => (
                    <option key={customer.id} value={customer.id}>
                      {customer.name} ({customer.email})
                    </option>
                  ))}
                </select>
              </div>

              {/* Chef Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Chef</label>
                <select
                  value={formData.chef_id}
                  onChange={(e) => setFormData({...formData, chef_id: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  required
                >
                  <option value="">Select Chef</option>
                  {chefs.map(chef => (
                    <option key={chef.chef_id} value={chef.chef_id}>
                      {chef.chef_name} - Max {chef.max_guests} guests
                    </option>
                  ))}
                </select>
              </div>

              {/* Tier Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Catering Tier</label>
                <select
                  value={formData.tier}
                  onChange={(e) => setFormData({...formData, tier: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  required
                >
                  <option value="basic">Basic - $75/plate (3 courses)</option>
                  <option value="premium">Premium - $100/plate (4 courses)</option>
                  <option value="deluxe">Deluxe - $150/plate (5 courses)</option>
                </select>
              </div>

              {/* Guest Count */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Guest Count (10-100)</label>
                <input
                  type="number"
                  min="10"
                  max="100"
                  value={formData.guest_count}
                  onChange={(e) => setFormData({...formData, guest_count: parseInt(e.target.value)})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  required
                />
              </div>

              {/* Date */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Event Date & Time</label>
                <input
                  type="datetime-local"
                  value={formData.date}
                  onChange={(e) => setFormData({...formData, date: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  required
                />
              </div>

              {/* Event Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Event Type</label>
                <select
                  value={formData.event_type}
                  onChange={(e) => setFormData({...formData, event_type: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                >
                  <option value="corporate">Corporate Event</option>
                  <option value="wedding">Wedding</option>
                  <option value="birthday">Birthday Party</option>
                  <option value="anniversary">Anniversary</option>
                  <option value="other">Other</option>
                </select>
              </div>

              {/* Address */}
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Service Address</label>
                <input
                  type="text"
                  value={formData.service_address}
                  onChange={(e) => setFormData({...formData, service_address: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  placeholder="123 Main St"
                  required
                />
              </div>

              {/* City, State, Zip */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                <input
                  type="text"
                  value={formData.service_city}
                  onChange={(e) => setFormData({...formData, service_city: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
                  <input
                    type="text"
                    value={formData.service_state}
                    onChange={(e) => setFormData({...formData, service_state: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    maxLength="2"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Zip</label>
                  <input
                    type="text"
                    value={formData.service_zip}
                    onChange={(e) => setFormData({...formData, service_zip: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    required
                  />
                </div>
              </div>

              {/* Special Requests */}
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Special Requests</label>
                <textarea
                  value={formData.special_requests}
                  onChange={(e) => setFormData({...formData, special_requests: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  rows="2"
                />
              </div>

              {/* Dietary Restrictions */}
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Dietary Restrictions</label>
                <textarea
                  value={formData.dietary_restrictions}
                  onChange={(e) => setFormData({...formData, dietary_restrictions: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  rows="2"
                />
              </div>
            </div>

            {/* Pricing Summary */}
            <div className="bg-gray-50 rounded-lg p-4 border-2 border-gray-200">
              <h4 className="font-bold text-lg mb-3">💰 Pricing Breakdown</h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span>{formData.guest_count} plates × ${totals.pricePerPlate}</span>
                  <span className="font-semibold">${totals.platesTotal}</span>
                </div>
                <div className="flex justify-between">
                  <span>Setup Fee</span>
                  <span className="font-semibold">${totals.setupFee}</span>
                </div>
                <div className="flex justify-between">
                  <span>Delivery Fee</span>
                  <span className="font-semibold">${totals.deliveryFee}</span>
                </div>
                <div className="border-t border-gray-300 pt-2 flex justify-between">
                  <span>Subtotal</span>
                  <span className="font-semibold">${totals.subtotal}</span>
                </div>
                <div className="flex justify-between text-orange-600">
                  <span>Operating Fee (20%)</span>
                  <span className="font-semibold">${totals.operatingFee}</span>
                </div>
                <div className="border-t-2 border-gray-400 pt-2 flex justify-between text-lg">
                  <span className="font-bold">Total Customer Pays</span>
                  <span className="font-bold text-orange-600">${totals.total}</span>
                </div>
              </div>
              
              {/* Payment Split */}
              <div className="mt-4 pt-4 border-t-2 border-gray-300 space-y-2">
                <p className="font-bold text-sm text-gray-700 mb-2">Payment Split:</p>
                <div className="flex justify-between text-sm bg-green-50 px-3 py-2 rounded">
                  <span className="font-semibold text-green-900">Chef Receives:</span>
                  <span className="font-bold text-green-600">${totals.platesTotal + totals.setupFee}</span>
                </div>
                <p className="text-xs text-green-700 ml-3">
                  ({formData.guest_count} plates × ${totals.pricePerPlate} = ${totals.platesTotal}) + Setup Fee ($300)
                </p>
                
                <div className="flex justify-between text-sm bg-blue-50 px-3 py-2 rounded">
                  <span className="font-semibold text-blue-900">Platform Receives:</span>
                  <span className="font-bold text-blue-600">${totals.deliveryFee + totals.operatingFee}</span>
                </div>
                <p className="text-xs text-blue-700 ml-3">
                  Delivery Fee ($125) + Operating Fee (${totals.operatingFee})
                </p>
              </div>
              
              <p className="text-xs text-gray-600 mt-3">
                Note: Grocery costs will be handled separately via issued card
              </p>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-orange-600 hover:bg-orange-700 disabled:bg-gray-400 text-white py-3 rounded-lg font-bold text-lg"
            >
              {loading ? 'Creating...' : 'Create Catering Booking'}
            </button>
          </form>
        </div>
      )}

      {/* Catering Bookings List */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-xl font-bold mb-4">Catering Bookings ({cateringBookings.length})</h3>
        {cateringBookings.length === 0 ? (
          <p className="text-gray-500 text-center py-8">No catering bookings yet</p>
        ) : (
          <div className="space-y-4">
            {cateringBookings.map(booking => (
              <div key={booking.id} className="border border-gray-200 rounded-lg p-4">
                <div className="flex justify-between items-start">
                  <div>
                    <h4 className="font-bold text-lg">{booking.customer_name}</h4>
                    <p className="text-sm text-gray-600">Chef: {booking.chef_name}</p>
                    <p className="text-sm text-gray-600">
                      {new Date(booking.date).toLocaleDateString()} at {new Date(booking.date).toLocaleTimeString()}
                    </p>
                    <p className="text-sm text-gray-600">
                      {booking.guest_count} guests • {booking.tier.charAt(0).toUpperCase() + booking.tier.slice(1)} tier
                    </p>
                    <p className="text-sm font-semibold text-orange-600 mt-1">
                      Total: ${booking.total}
                    </p>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                    booking.status === 'confirmed' ? 'bg-green-100 text-green-800' :
                    booking.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                    booking.status === 'completed' ? 'bg-blue-100 text-blue-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {booking.status}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminCateringPanel;
